import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export type EshopProductMapKind = "voucher" | "merch";

export interface EshopProductMapRow {
  id: number;
  eshop_product_id: string;
  kind: EshopProductMapKind;
  voucher_type_id: number | null;
  merch_kind_id: number | null;
  default_quantity: number;
}

export interface EshopProductMapListResponse {
  items: EshopProductMapRow[];
}

export interface UpsertEshopProductMapPayload {
  eshop_product_id: string;
  kind: EshopProductMapKind;
  voucher_type_id?: number | null;
  merch_kind_id?: number | null;
  default_quantity: number;
}

// --------------------------------------------------------------------------
// Queries
// --------------------------------------------------------------------------

export function useEshopProductMapList() {
  return useQuery<EshopProductMapListResponse>({
    queryKey: ["eshop-product-map"],
    queryFn: () => apiGet("/eshop-product-map"),
  });
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export function useCreateEshopProductMap() {
  const qc = useQueryClient();
  return useMutation<EshopProductMapRow, unknown, UpsertEshopProductMapPayload>({
    mutationFn: (payload) => apiPost("/eshop-product-map", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["eshop-product-map"] }),
  });
}

export function useUpdateEshopProductMap() {
  const qc = useQueryClient();
  return useMutation<
    EshopProductMapRow,
    unknown,
    { id: number; payload: UpsertEshopProductMapPayload }
  >({
    mutationFn: ({ id, payload }) => apiPut(`/eshop-product-map/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["eshop-product-map"] }),
  });
}

export function useDeleteEshopProductMap() {
  const qc = useQueryClient();
  return useMutation<void, unknown, number>({
    mutationFn: (id) => apiDelete(`/eshop-product-map/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["eshop-product-map"] }),
  });
}
